import React from 'react';
import { spnColor } from '../../../lib/j1939FrameDecoder';

const BITS_MSB_FIRST = [7, 6, 5, 4, 3, 2, 1, 0];
const OVERLAP = 'repeating-linear-gradient(45deg,#dc2626,#dc2626 4px,#7f1d1d 4px,#7f1d1d 8px)';
const hex2 = (b) => b.toString(16).toUpperCase().padStart(2, '0');

// Which payload bits each SPN of one frame uses, one colour per SPN row. Categorical (bytes x
// bits), so no chart zoom. Bit positions come from the backend layouts, so the picture always
// matches the decode and the DBC.
const SpnBitMap = ({ spns, dataBytes, testId }) => {
  const owners = new Map(); // "byte-bit" -> [{k, name, significance, msb, lsb}]
  let lastByte = -1;
  spns.forEach(({ k, name, layout }) => {
    if (!layout) return;
    layout.bits.forEach((b) => {
      const key = `${b.byte}-${b.bit}`;
      const isMsb = layout.msb.byte === b.byte && layout.msb.bit === b.bit;
      const isLsb = layout.lsb.byte === b.byte && layout.lsb.bit === b.bit;
      owners.set(key, [...(owners.get(key) || []), { k, name, significance: b.significance, isMsb, isLsb }]);
      lastByte = Math.max(lastByte, b.byte);
    });
  });
  const payload = dataBytes ? dataBytes.length : 8;
  const total = Math.min(Math.max(payload, lastByte + 1, 1), 8);
  const used = spns.filter((s) => s.layout);
  const overlapCount = [...owners.values()].filter((o) => o.length > 1).length;
  const unusedCount = payload * 8 - [...owners.keys()].filter((key) => Number(key.split('-')[0]) < payload).length;

  const cell = (byte, bit) => {
    const own = owners.get(`${byte}-${bit}`);
    const beyond = byte >= payload;
    const value = dataBytes && byte < dataBytes.length ? (dataBytes[byte] >> bit) & 1 : null;
    let style;
    let cls = 'bg-background text-muted-foreground border-structure';
    if (own && own.length > 1) {
      cls = 'text-white border-red-700';
      style = { background: OVERLAP };
    } else if (own && beyond) {
      cls = 'bg-red-100 text-red-700 border-red-500 border-dashed dark:bg-red-950/40 dark:text-red-300';
    } else if (own) {
      const c = spnColor(own[0].k);
      cls = 'border-transparent';
      style = { background: c.bg, color: c.fg };
    }
    const tag = own && own.length === 1 && (own[0].isMsb || own[0].isLsb)
      ? (own[0].isMsb && own[0].isLsb ? 'M/L' : own[0].isMsb ? 'MSB' : 'LSB') : null;
    const title = [
      `Byte ${byte}, bit ${bit} (DBC bit ${byte * 8 + bit})`,
      own ? own.map((o) => `${o.name || `SPN row ${o.k + 1}`} bit ${o.significance}`).join(' + ') : 'Unused',
      own && own.length > 1 ? 'OVERLAP' : null,
      beyond ? 'Outside the payload' : null,
      value === null ? null : `Value ${value}`,
    ].filter(Boolean).join(' · ');
    return (
      <div key={bit} className="flex flex-col items-center gap-0.5">
        <div
          title={title} style={style}
          className={`relative h-7 w-7 sm:h-8 sm:w-8 flex items-center justify-center rounded border text-xs font-mono font-bold ${cls}`}
          data-testid={`${testId}-cell-${byte}-${bit}`}
          data-owner={own ? own.map((o) => o.k + 1).join(',') : undefined}
        >
          {value === null ? '·' : value}
          {tag && <span className="absolute -top-2 -right-1.5 rounded-sm bg-foreground text-background px-0.5 text-[9px] leading-tight">{tag}</span>}
        </div>
        <span className="text-[9px] font-mono text-muted-foreground/70">{byte * 8 + bit}</span>
      </div>
    );
  };

  return (
    <div className="space-y-3" data-testid={testId}>
      <div className="flex flex-wrap gap-3">
        {Array.from({ length: total }, (_, byte) => {
          const beyond = byte >= payload;
          const value = dataBytes && byte < dataBytes.length ? dataBytes[byte] : null;
          return (
            <div key={byte} className={`rounded-md border p-2 ${beyond ? 'border-dashed border-red-400 bg-red-50/50 dark:bg-red-950/20' : 'border-structure bg-muted/30'}`} data-testid={`${testId}-byte-${byte}`}>
              <div className="flex items-center justify-between mb-2 font-mono text-xs">
                <span className="font-bold">Byte {byte}</span>
                <span className={beyond ? 'text-red-600' : 'text-muted-foreground'}>{beyond ? 'outside payload' : value === null ? '--' : `0x${hex2(value)}`}</span>
              </div>
              <div className="flex gap-0.5 sm:gap-1 pt-1">{BITS_MSB_FIRST.map((bit) => cell(byte, bit))}</div>
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-muted-foreground" data-testid={`${testId}-legend`}>
        {used.map(({ k, name }) => (
          <span key={k} className="flex items-center gap-1.5">
            <span className="h-3 w-3 rounded-sm" style={{ background: spnColor(k).bg }} /> {name || `SPN row ${k + 1}`}
          </span>
        ))}
        {overlapCount > 0 && (
          <span className="flex items-center gap-1.5 text-destructive" data-testid={`${testId}-overlap`}>
            <span className="h-3 w-3 rounded-sm" style={{ background: 'repeating-linear-gradient(45deg,#dc2626,#dc2626 2px,#7f1d1d 2px,#7f1d1d 4px)' }} />
            Overlap ({overlapCount} bit{overlapCount === 1 ? '' : 's'})
          </span>
        )}
        <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm border border-structure bg-background" /> Unused ({Math.max(0, unusedCount)} bits)</span>
        <span>Small numbers = DBC bit number (byte × 8 + bit)</span>
      </div>
    </div>
  );
};

export default SpnBitMap;
